import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cleanParams, encodeArrayObjects } from "../Utilities/queryParamUtils";
import {
  ArchivedTicketsActionType,
  ArchiveTicketType,
  FetchApprovedTicketType,
  FetchArchivedTicketsType,
  FetchInvoiceMetaType,
  TicketProcessingCountMetaType,
} from "../Types/ticketProcessingTypes";
import { MutateKeys } from "../constants/MutateKeys";
import { generatePath } from "react-router-dom";

interface TicketProcessingListingProps {
  companyId?: string;
  status?: string;
  jobId?: string;
  truckId?: string;
  startDate?: string;
  endDate?: string;
  currentPage?: string;
  pageSize?: string;
  userId?: string;
}

interface ReplaceTicketPayloadType {
  id: string;
  ticketUploadPicture: string;
  replaceReason: string;
}

interface TicketProcessingReviewListProps extends Omit<
  TicketProcessingListingProps,
  "currentPage" | "pageSize"
> {
  search?: string;
}

const TicketProcessingServices = () => {
  const {
    FETCH_TICKET_PROCESSING_LISTING,
    FETCH_TICKET_PROCESSING_FILTERS,
    FETCH_APPROVED_TICKETS,
    FETCH_INVOICE_META,
    FETCH_ARCHIVED_TICKETS,
    PATCH_ARCHIVED_TICKET,
    FETCH_TICKET_PROCESSING_REVIEW_LIST,
    FETCH_TICKET_PROCESSING_TICKET_DETAILS,
    SUBMIT_TICKET_FOR_PROCESSING,
    FETCH_TICKET_PROCESSING_SECTION_COUNT,
    REPLACE_TKT_IN_REVIEW,
    ARCHIVE_TICKET,
    GENERATE_INVOICES,
  } = ApiRoutes;
  const {
    FETCH_TICKET_PROCESSING_KEY,
    FETCH_TICKET_PROCESSING_FILTERS_KEY,
    FETCH_APPROVED_TICKETS_KEY,
    FETCH_ARCHIVED_TICKETS_KEY,
    FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY,
    FETCH_TICKET_PROCESSING_DETAILS_KEY,
    FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY,
  } = QueryKeys;
  const {
    SUBMIT_TICKET_PROCESSING_DETAILS_KEY,
    PATCH_ARCHIVED_TICKET_KEY,
    REPLACE_REVIEW_TICKET_KEY,
    ARCHIVE_TICKET_KEY,
    FETCH_INVOICE_META_KEY,
    GENERATE_INVOICES_KEY,
  } = MutateKeys;

  const queryClient = useQueryClient();
  const keysToInvalidate = new Set([
    FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY,
    FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY,
    FETCH_ARCHIVED_TICKETS_KEY,
  ]);

  const FetchTicketProcessingList = (
    params?: TicketProcessingListingProps,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const fetchTicketProcessingList = async () => {
      const cleanedParams = cleanParams({
        ...params,
        setDefaultPageSize: pageSizeChanged,
      });
      const { data } = await axiosInstance.get(
        FETCH_TICKET_PROCESSING_LISTING,
        {
          params: {
            ...cleanedParams,
          },
        },
      );
      return data;
    };

    return useQuery({
      queryKey: [FETCH_TICKET_PROCESSING_KEY],
      queryFn: fetchTicketProcessingList,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const FetchTicketProcessingFilterMeta = () => {
    const fetchTicketFilterOptions = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_PROCESSING_FILTERS);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_PROCESSING_FILTERS_KEY],
      queryFn: fetchTicketFilterOptions,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchApprovedTickets = (params?: FetchApprovedTicketType) => {
    const fetchApprovedTickets = async () => {
      const cleanedParams = cleanParams(params);
      const encodedParams = encodeArrayObjects(cleanedParams);
      const { data } = await axiosInstance.get(FETCH_APPROVED_TICKETS, {
        params: {
          ...encodedParams,
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_APPROVED_TICKETS_KEY],
      queryFn: fetchApprovedTickets,
      refetchOnWindowFocus: false,
    });
  };

  const FetchInvoiceMeta = () => {
    const fetchInvoiceMeta = async (dateList?: FetchInvoiceMetaType[]) => {
      const payload = {
        entityData: {
          dateRanges: dateList,
        },
      };
      const { data } = await axiosInstance.post(FETCH_INVOICE_META, payload);
      return data;
    };
    return useMutation({
      mutationKey: [FETCH_INVOICE_META_KEY],
      mutationFn: fetchInvoiceMeta,
    });
  };

  const FetchArchivedTickets = (params?: FetchArchivedTicketsType) => {
    const fetchArchivedTickets = async () => {
      const cleanedParams = cleanParams(params);
      const encodedParams = encodeArrayObjects(cleanedParams);
      const { data } = await axiosInstance.get(FETCH_ARCHIVED_TICKETS, {
        params: {
          ...encodedParams,
        },
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_ARCHIVED_TICKETS_KEY],
      queryFn: fetchArchivedTickets,
      refetchOnWindowFocus: false,
    });
  };

  const FetchTicketProcessingReviewList = (
    params?: TicketProcessingReviewListProps,
  ) => {
    const fetchTicketProcessingReviewList = async () => {
      const cleanedParams = cleanParams(params);
      const { data } = await axiosInstance.get(
        FETCH_TICKET_PROCESSING_REVIEW_LIST,
        {
          params: {
            ...cleanedParams,
          },
        },
      );
      return data?.data;
    };

    return useQuery({
      queryKey: [FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY, params?.status],
      queryFn: fetchTicketProcessingReviewList,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const FetchTicketProcessingReviewDetails = ({
    id,
    isSubmitted = false,
  }: {
    id: string;
    isSubmitted: boolean;
  }) => {
    const fetchTicketProcessingReviewDetails = async () => {
      const url = generatePath(FETCH_TICKET_PROCESSING_TICKET_DETAILS, {
        id: id,
      });
      const { data } = await axiosInstance.get(url, {
        params: {
          ...(isSubmitted && { isSubmitted: true }),
        },
      });
      return data?.data;
    };

    return useQuery({
      queryKey: [FETCH_TICKET_PROCESSING_DETAILS_KEY, id, isSubmitted],
      queryFn: fetchTicketProcessingReviewDetails,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const ArchivedTicketActions = () => {
    const archivedTicketsActions = async (
      params?: ArchivedTicketsActionType,
    ) => {
      const payload = {
        entityData: {
          ...params,
        },
      };
      const { data } = await axiosInstance.post(PATCH_ARCHIVED_TICKET, payload);
      return data;
    };
    return useMutation({
      mutationFn: archivedTicketsActions,
      mutationKey: [PATCH_ARCHIVED_TICKET_KEY],
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [FETCH_ARCHIVED_TICKETS_KEY],
        });
      },
    });
  };

  const SubmitTicketProcessingReview = () => {
    const submitTicketProcessingReview = async (submitForDataInputBody) => {
      const { id, ...dataInputPayload } = submitForDataInputBody;
      const payload = {
        entityData: cleanParams({
          ...dataInputPayload,
        }),
      };
      const url = generatePath(SUBMIT_TICKET_FOR_PROCESSING, { id: id });

      const { data } = await axiosInstance.put(url, payload);
      return data;
    };

    return useMutation({
      mutationKey: [SUBMIT_TICKET_PROCESSING_DETAILS_KEY],
      mutationFn: submitTicketProcessingReview,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY], // by default invalidates the partial matching key 
        });
      },
    });
  };

  const FetchTicketProcessingSectionCountMeta = (
    payload: TicketProcessingCountMetaType,
  ) => {
    const fetchTicketProcessingSectionCountMeta = async () => {
      const cleanedParams = cleanParams(payload);
      const { data } = await axiosInstance.get(
        FETCH_TICKET_PROCESSING_SECTION_COUNT,
        {
          params: {
            ...cleanedParams,
          },
        },
      );
      return data?.data;
    };

    return useQuery({
      queryKey: [FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY],
      queryFn: fetchTicketProcessingSectionCountMeta,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const ReplaceTicket = () => {
    const replaceTicket = async (
      replaceTicketPayload: ReplaceTicketPayloadType,
    ) => {
      const { id, ...payloadValues } = replaceTicketPayload;
      const payload = {
        entityData: {
          ...payloadValues,
        },
      };
      const { data } = await axiosInstance.post(
        generatePath(REPLACE_TKT_IN_REVIEW, { id: id }),
        payload,
      );
      return data;
    };
    return useMutation({
      mutationKey: [REPLACE_REVIEW_TICKET_KEY],
      mutationFn: replaceTicket,
    });
  };

  const ArchiveTicket = () => {
    const archiveTicket = async (payload: ArchiveTicketType) => {
      const { id } = payload;
      const url = generatePath(ARCHIVE_TICKET, {
        id: id,
      });
      const { data } = await axiosInstance.patch(url);
      return data;
    };
    return useMutation({
      mutationKey: [ARCHIVE_TICKET_KEY],
      mutationFn: archiveTicket,
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => keysToInvalidate.has(query.queryKey[0]),
        });
      },
    });
  };

  const GenerateInvoices = () => {
    const generateInvoices = async (rangeList: FetchInvoiceMetaType[]) => {
      const payload = {
        entityData: rangeList,
      };
      const { data } = await axiosInstance.post(GENERATE_INVOICES, payload);
      return data;
    };
    return useMutation({
      mutationKey: [GENERATE_INVOICES_KEY],
      mutationFn: generateInvoices,
    });
  };

  return {
    FetchTicketProcessingList,
    FetchTicketProcessingFilterMeta,
    FetchApprovedTickets,
    FetchInvoiceMeta,
    FetchArchivedTickets,
    ArchivedTicketActions,
    FetchTicketProcessingReviewList,
    FetchTicketProcessingReviewDetails,
    SubmitTicketProcessingReview,
    FetchTicketProcessingSectionCountMeta,
    ReplaceTicket,
    ArchiveTicket,
    GenerateInvoices,
  };
};

export default TicketProcessingServices;
